import React, { useEffect, useRef, useState } from 'react'
import { AppDialog, Select } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import { RecordFieldInput } from './RecordFieldInput'
import { normalizeRecordValues } from './normalizeRecordValues'
import {
  submitRecord,
  subscribeToTemplate,
  subscribeToTemplateVersions,
} from '../../../services/opsRegistersService'
import { uploadRecordPhoto } from '../../../services/opsRecordPhotosService'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { storeSharedDeviceBlob } from '../../../services/sharedDeviceOutbox'
import { getStoredSharedDeviceCredentials } from '../../../services/opsSharedDevicesService'
import type { OpsPlace, OpsTemplate, OpsTemplateField, OpsTemplateVersion } from '../../../types/ops'
import styles from './OpsRecordModalForm.module.css'

export interface OpsRecordModalFormProps {
  open: boolean
  workspaceId: string
  templateId?: string
  placeId?: string
  dueId?: string
  templates?: OpsTemplate[]
  places?: OpsPlace[]
  title?: string
  submitLabel?: string
  formTestId?: string
  submitTestId?: string
  fieldTestIdPrefix?: string
  onDismiss: () => void
  onSubmit?: (payload: {
    workspaceId: string
    templateId: string
    placeId: string
    dueId?: string
    values: Record<string, unknown>
    occurredAt: number
    capturedAt: number
    opId: string
    recordId?: string
    photoBindings?: Array<{ fieldKey: string; blobKey: string; fileName?: string; contentType?: string }>
  }) => Promise<void>
  onSuccess?: () => void
  isSharedDevice?: boolean
  devicePlaceId?: string
  cachedTemplate?: OpsTemplate | null
  cachedVersion?: OpsTemplateVersion | null
}

export function OpsRecordModalForm({
  open,
  workspaceId,
  templateId: initialTemplateId,
  placeId: initialPlaceId,
  dueId,
  templates = [],
  places = [],
  title: customTitle,
  submitLabel,
  formTestId = 'due-record-form',
  submitTestId,
  fieldTestIdPrefix,
  onDismiss,
  onSubmit,
  onSuccess,
  isSharedDevice,
  devicePlaceId,
  cachedTemplate,
  cachedVersion,
}: OpsRecordModalFormProps): React.ReactElement | null {
  const { t } = useNls()
  const fieldPrefix = fieldTestIdPrefix ?? (dueId ? 'due' : 'field')

  const [userSelectedTemplateId, setUserSelectedTemplateId] = useState<string>('')
  const [userSelectedPlaceId, setUserSelectedPlaceId] = useState<string>('')

  const effectiveTemplateId = initialTemplateId || userSelectedTemplateId || (templates[0]?.id ?? '')
  const effectivePlaceId =
    (isSharedDevice && devicePlaceId) ||
    initialPlaceId ||
    userSelectedPlaceId ||
    (places[0]?.id ?? '')

  const [activeTemplate, setActiveTemplate] = useState<OpsTemplate | null>(() => cachedTemplate ?? null)
  const [templateFields, setTemplateFields] = useState<OpsTemplateField[]>(() =>
    cachedVersion?.fields && Array.isArray(cachedVersion.fields) ? cachedVersion.fields : [],
  )
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [formBusy, setFormBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [photoUrlsByPath, setPhotoUrlsByPath] = useState<Record<string, string>>({})
  const [photoBindings, setPhotoBindings] = useState<
    Array<{ fieldKey: string; blobKey: string; fileName: string; contentType: string }>
  >([])
  const [recordDraftId, setRecordDraftId] = useState<string | null>(null)
  const pendingDraftOpIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!effectiveTemplateId) {
      return undefined
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine && cachedTemplate) {
      return undefined
    }

    const unsubVersions = subscribeToTemplateVersions(
      effectiveTemplateId,
      (versions) => {
        const latest = versions[versions.length - 1]
        setTemplateFields(
          latest?.fields && Array.isArray(latest.fields) ? latest.fields : [],
        )
      },
      undefined,
      { approvedOnly: true },
    )

    const unsubTemplate = subscribeToTemplate(effectiveTemplateId, (tpl) => {
      setActiveTemplate(tpl)
    })

    return () => {
      unsubVersions()
      unsubTemplate()
    }
  }, [effectiveTemplateId, cachedTemplate])

  const effectiveTemplateFields = effectiveTemplateId ? templateFields : []
  const effectiveActiveTemplate = effectiveTemplateId ? activeTemplate : null

  const storedCreds = isSharedDevice ? getStoredSharedDeviceCredentials() : null
  const storedDeviceId = storedCreds?.deviceId?.trim() || null

  const handlePhotoSelect = async (fieldKey: string, files: FileList) => {
    const file = files[0]
    if (!file || !effectiveTemplateId || !effectivePlaceId) return
    if (typeof navigator !== 'undefined' && !navigator.onLine && (!isSharedDevice || !storedDeviceId)) {
      setError(t('NLSHelperOpsPhotoOfflineDisabled'))
      return
    }
    setFormBusy(true)
    setError(null)
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine && isSharedDevice && storedDeviceId) {
        const deviceId = storedDeviceId
        const blobKey = `blob-rec-${crypto.randomUUID()}`

        await storeSharedDeviceBlob(deviceId, {
          blobKey,
          blob: file,
          contentType: file.type,
          size: file.size,
          createdAt: Date.now(),
        })

        const localUrl = typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : ''
        if (localUrl) {
          setPhotoUrlsByPath((prev) => ({
            ...prev,
            [blobKey]: localUrl,
          }))
        }

        setPhotoBindings((prev) => [
          ...prev,
          { fieldKey, blobKey, fileName: file.name, contentType: file.type },
        ])

        setFormValues((prev) => {
          const existing = prev[fieldKey]
          const prevArr = Array.isArray(existing)
            ? existing
            : typeof existing === 'string' && existing
              ? [existing]
              : []
          return { ...prev, [fieldKey]: [...prevArr, blobKey] }
        })
        return
      }

      let activeRecordId = recordDraftId
      if (!activeRecordId) {
        const nowDate = new Date()
        if (!pendingDraftOpIdRef.current) {
          pendingDraftOpIdRef.current = crypto.randomUUID()
        }
        const draftRes = await submitRecord({
          workspaceId,
          templateId: effectiveTemplateId,
          placeId: effectivePlaceId,
          occurredAt: nowDate.getTime(),
          capturedAt: nowDate.getTime(),
          opId: pendingDraftOpIdRef.current,
          values: normalizeRecordValues(formValues, templateFields),
          asDraft: true,
        })
        pendingDraftOpIdRef.current = null
        activeRecordId = draftRes.recordId
        setRecordDraftId(activeRecordId)
      }
      const uploaded = await uploadRecordPhoto(
        workspaceId,
        activeRecordId,
        file,
      )
      setFormValues((prev) => {
        const existing = prev[fieldKey]
        const prevArr = Array.isArray(existing)
          ? existing
          : typeof existing === 'string' && existing
            ? [existing]
            : []
        return { ...prev, [fieldKey]: [...prevArr, uploaded.path] }
      })
      if (uploaded.downloadUrl) {
        setPhotoUrlsByPath((prev) => ({
          ...prev,
          [uploaded.path]: uploaded.downloadUrl,
        }))
      }
    } catch (err) {
      const code = getApiErrorMessage(err)
      if (code === 'ops_photo_invalid_type') {
        setError(t('NLSOpsPhotoInvalidType'))
      } else if (code === 'ops_photo_too_large') {
        setError(t('NLSOpsPhotoTooLarge'))
      } else {
        setError(code)
      }
    } finally {
      setFormBusy(false)
    }
  }

  const handleSubmitForm = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!effectiveTemplateId) {
      setError(t('NLSOpsSelectTemplate'))
      return
    }
    if (!effectivePlaceId) {
      setError(t('NLSOpsSelectPlace'))
      return
    }

    for (const field of templateFields) {
      if (field.required) {
        const val = formValues[field.key]
        let empty = val === undefined || val === null || val === ''
        if (field.type === 'PHOTO') {
          empty = !val || (Array.isArray(val) && val.length === 0)
        } else if (field.type === 'ACKNOWLEDGE') {
          empty = !val
        } else if (field.type === 'CASH_COUNT') {
          empty = !val || typeof val !== 'object'
        }
        if (empty) {
          const fieldLabel = field.label || field.key
          setError(t('NLSHelperOpsRequiredFieldMissing', { field: fieldLabel }))
          return
        }
      }
    }

    setFormBusy(true)
    setError(null)
    const opId = crypto.randomUUID()
    const ts = Date.now()

    const payload = {
      workspaceId,
      templateId: effectiveTemplateId,
      placeId: effectivePlaceId,
      ...(dueId ? { dueId } : {}),
      values: normalizeRecordValues(formValues, templateFields),
      occurredAt: ts,
      capturedAt: ts,
      opId,
      ...(recordDraftId ? { recordId: recordDraftId } : {}),
      ...(photoBindings.length > 0 ? { photoBindings } : {}),
    }

    try {
      if (onSubmit) {
        await onSubmit(payload)
      } else {
        await submitRecord(payload)
      }
      onSuccess?.()
      onDismiss()
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setFormBusy(false)
    }
  }

  if (!open) return null

  const resolvedTitle =
    customTitle ||
    effectiveActiveTemplate?.title ||
    effectiveActiveTemplate?.name ||
    effectiveTemplateId ||
    t('NLSHelperOpsRecordNowBtn')

  const effectiveSubmitLabel = submitLabel || t('NLSHelperOpsRecordNowBtn')
  const effectiveSubmitTestId = submitTestId || (dueId ? `due-record-submit-${dueId}` : undefined)

  return (
    <AppDialog
      open={open}
      title={resolvedTitle}
      onDismiss={onDismiss}
      actions={[
        {
          label: t('NLSCommonActionCancel'),
          variant: 'secondary',
          onClick: onDismiss,
          disabled: formBusy,
        },
        {
          label: effectiveSubmitLabel,
          variant: 'primary',
          onClick: () => void handleSubmitForm(),
          disabled: formBusy,
          testId: effectiveSubmitTestId,
        },
      ]}
    >
      <form
        data-testid={formTestId}
        onSubmit={(e) => void handleSubmitForm(e)}
        className={styles.dialogBody}
      >
        {error && (
          <p className={styles.fieldError} role="alert">
            {error}
          </p>
        )}

        {!initialTemplateId && templates.length > 0 && (
          <div className={styles.formField}>
            <Select
              id="ops-record-modal-template-select"
              label={t('NLSOpsSelectTemplate')}
              ariaLabel={t('NLSOpsSelectTemplate')}
              value={effectiveTemplateId}
              onChange={(val) => setUserSelectedTemplateId(val)}
              options={templates.map((tpl) => ({
                value: tpl.id ?? '',
                label: tpl.name,
              }))}
            />
          </div>
        )}

        {!initialPlaceId && places.length > 0 && (
          <div className={styles.formField}>
            <Select
              id="ops-record-modal-place-select"
              label={t('NLSOpsSelectPlace')}
              ariaLabel={t('NLSOpsSelectPlace')}
              value={effectivePlaceId}
              onChange={(val) => setUserSelectedPlaceId(val)}
              options={places.map((pl) => ({
                value: pl.id ?? '',
                label: pl.name,
              }))}
            />
          </div>
        )}

        {effectiveTemplateFields.length === 0 ? (
          <p className={styles.emptyText}>{t('NLSHelperOpsNoFieldsInTemplate')}</p>
        ) : (
          effectiveTemplateFields.map((field) => {
            const isOfflinePhoto =
              field.type === 'PHOTO' &&
              typeof navigator !== 'undefined' &&
              !navigator.onLine &&
              (!isSharedDevice || !storedDeviceId)
            return (
              <div key={field.key} className={styles.formField}>
                <label htmlFor={`${fieldPrefix}-input-${field.key}`} className={styles.fieldLabel}>
                  {field.label || field.key}
                  {field.required ? ' *' : ''}
                </label>
                {isOfflinePhoto && (
                  <p className={styles.fieldError}>{t('NLSHelperOpsPhotoOfflineDisabled')}</p>
                )}
                <RecordFieldInput
                  field={field}
                  value={formValues[field.key]}
                  onChange={(val) =>
                    setFormValues((prev) => ({ ...prev, [field.key]: val }))
                  }
                  inputId={`${fieldPrefix}-input-${field.key}`}
                  onPhotoSelect={(fieldKey, files) => void handlePhotoSelect(fieldKey, files)}
                  photoUrlsByPath={photoUrlsByPath}
                />
              </div>
            )
          })
        )}
      </form>
    </AppDialog>
  )
}
